/**
 * Shared timeline math for the Edit Timeline modal.
 * Keep in sync with mobile/src/utils/timelineStrip.js.
 */

/** Thumbnail width for a 2-second cut; 1-second cuts render at half this. */
export const TIMELINE_CUT_BASE_WIDTH = 72;
export const TIMELINE_CUT_THUMB_HEIGHT = Math.round(TIMELINE_CUT_BASE_WIDTH * (16 / 9));
export const TIMELINE_PX_PER_EFFECTIVE_SECOND = TIMELINE_CUT_BASE_WIDTH / 2;
export const TIMELINE_STRIP_PADDING = 2;
export const TIMELINE_CUT_BORDER = 1;
export const TIMELINE_CUT_GAP = 1;

/** Effective on-timeline seconds per cut (fast pacing trims 2s assets to 1s). */
export function timelineCutEffectiveSeconds(cut, fastPacing) {
  const d = (typeof cut?.duration === 'number' && cut.duration >= 2) ? 2 : 1;
  return fastPacing && d === 2 ? 1 : d;
}

export function sumTimelineEffectiveSeconds(cuts, fastPacing) {
  return (cuts || []).reduce((acc, c) => acc + timelineCutEffectiveSeconds(c, fastPacing), 0);
}

/** Count how many times each cut id appears on the timeline (for add-cut picker badges). */
export function timelineCutUsageCounts(cuts) {
  const counts = new Map();
  for (const c of cuts || []) {
    const id = String(c?._id ?? c?.id ?? '');
    if (!id) continue;
    counts.set(id, (counts.get(id) || 0) + 1);
  }
  return counts;
}

/** Active cut index from voiceover currentTime (same pacing rules as labels and widths). */
export function timelineActiveCutIndexFromTime(cuts, timeSeconds, fastPacing) {
  const n = (cuts || []).length;
  if (n === 0) return null;
  let acc = 0;
  let idx = n - 1;
  for (let i = 0; i < n; i++) {
    const end = acc + timelineCutEffectiveSeconds(cuts[i], fastPacing);
    if (timeSeconds < end) {
      idx = i;
      break;
    }
    acc = end;
  }
  return idx;
}

/**
 * Insert index for a new cut at the playhead: before the active cut when the
 * playhead is in the left half (< 50%), after when in the right half.
 */
export function timelineInsertIndexAtPlayhead(cuts, timeSeconds, fastPacing) {
  const n = (cuts || []).length;
  if (n === 0) return 0;

  let acc = 0;
  for (let i = 0; i < n; i++) {
    const cutSec = timelineCutEffectiveSeconds(cuts[i], fastPacing);
    const end = acc + cutSec;
    if (timeSeconds < end || i === n - 1) {
      const progress = cutSec > 0 ? Math.min(1, Math.max(0, (timeSeconds - acc) / cutSec)) : 0;
      return progress < 0.5 ? i : i + 1;
    }
    acc = end;
  }
  return n;
}

/**
 * Playhead time slightly past the middle of the cut at `cutIndex`, so the next
 * add-cut lands after that newly inserted cut.
 */
export function timelinePlayheadJustPastMidCut(cuts, cutIndex, fastPacing) {
  const list = cuts || [];
  if (!list.length || cutIndex < 0 || cutIndex >= list.length) return 0;
  let acc = 0;
  for (let i = 0; i < cutIndex; i++) {
    acc += timelineCutEffectiveSeconds(list[i], fastPacing);
  }
  const cutSec = timelineCutEffectiveSeconds(list[cutIndex], fastPacing);
  return acc + cutSec * 0.55;
}

export function timelineCutThumbWidth(cut, fastPacing) {
  return timelineCutEffectiveSeconds(cut, fastPacing) * TIMELINE_PX_PER_EFFECTIVE_SECOND;
}

/** Outer card width: thumb + left/right border. */
export function timelineCutCardWidth(cut, fastPacing) {
  return timelineCutThumbWidth(cut, fastPacing) + TIMELINE_CUT_BORDER * 2;
}

/** Width of the cuts track only (variable card widths + gaps, no strip padding). */
export function timelineCutsTrackWidth(cuts, fastPacing) {
  if (!cuts?.length) return 0;
  return cuts.reduce(
    (acc, cut, index) => acc + timelineCutCardWidth(cut, fastPacing) + (index > 0 ? TIMELINE_CUT_GAP : 0),
    0
  );
}

/** Inverse of timelinePlayheadLeft: voiceover time from a track-relative x (clamped to [0, total]). */
export function timelineTimeFromTrackLeft(cuts, px, fastPacing) {
  if (!cuts?.length) return 0;
  let accTime = 0;
  let accPx = 0;
  for (let i = 0; i < cuts.length; i++) {
    const cutSec = timelineCutEffectiveSeconds(cuts[i], fastPacing);
    const cardW = timelineCutCardWidth(cuts[i], fastPacing);
    const thumbW = timelineCutThumbWidth(cuts[i], fastPacing);
    const cardStart = accPx + (i > 0 ? TIMELINE_CUT_GAP : 0);
    const thumbStart = cardStart + TIMELINE_CUT_BORDER;
    if (px <= thumbStart + thumbW || i === cuts.length - 1) {
      const progress = thumbW > 0 ? Math.min(1, Math.max(0, (px - thumbStart) / thumbW)) : 0;
      return accTime + progress * cutSec;
    }
    accTime += cutSec;
    accPx = cardStart + cardW;
  }
  return accTime;
}

/** Playhead X relative to the track start, mapped to voiceover time across variable-width cuts. */
export function timelinePlayheadLeft(cuts, timeSeconds, fastPacing) {
  if (!cuts?.length) return 0;
  let accTime = 0;
  let accPx = 0;
  for (let i = 0; i < cuts.length; i++) {
    const cutSec = timelineCutEffectiveSeconds(cuts[i], fastPacing);
    const cardW = timelineCutCardWidth(cuts[i], fastPacing);
    const thumbW = timelineCutThumbWidth(cuts[i], fastPacing);
    const cardStart = accPx + (i > 0 ? TIMELINE_CUT_GAP : 0);
    if (timeSeconds <= accTime + cutSec) {
      const progress = cutSec > 0 ? Math.min(1, Math.max(0, (timeSeconds - accTime) / cutSec)) : 0;
      return cardStart + TIMELINE_CUT_BORDER + progress * thumbW;
    }
    accTime += cutSec;
    accPx = cardStart + cardW;
  }
  return accPx;
}

/**
 * Pixel width of the voiceover audio lane.
 * Aligns with the playhead at audio-end when cuts cover that time; otherwise keeps a
 * duration-stable width so adding/removing cuts doesn't make the VO appear to change length.
 */
export function timelineAudioLaneWidth(cuts, audioDuration, fastPacing) {
  const trackW = timelineCutsTrackWidth(cuts, fastPacing);
  if (!(audioDuration > 0)) return trackW;
  const totalSec = sumTimelineEffectiveSeconds(cuts, fastPacing);
  if (totalSec > 0 && audioDuration <= totalSec + 1e-6) {
    return Math.min(trackW, timelinePlayheadLeft(cuts, audioDuration, fastPacing));
  }
  if (totalSec > 0) {
    return trackW + (audioDuration - totalSec) * TIMELINE_PX_PER_EFFECTIVE_SECOND;
  }
  return audioDuration * TIMELINE_PX_PER_EFFECTIVE_SECOND;
}
